/**
 * @file index.tsx
 *
 * @fileoverview Helper functions for job analytics events.
 */

import { IntlShape } from 'react-intl';
import {
  DIMENSIONS,
  PAGE_TYPES,
  PageType,
  setEventParams,
} from '~/jobiqo/lib/analytics';
import { User } from '~/jobiqo/models/user';
import { Job } from '~/jobiqo/types/types';
import { concatArrayItems } from '~/jobiqo/utils/mapper';
import { getCustomJobDimensions } from '~/jobiqo/components/Slots/customAnalyticsDimensions';
import { parseUniversalDate } from '~/jobiqo/utils/dates';

/**
 * Returns the dimensions for a given job.
 *
 * @param {Job} job
 *   The job to return dimensions for.
 * @param {User|null} user
 *   The current user.
 * @returns {Object}
 *   The dimensions object for this job.
 */
export const getJobDimensions = (
  job: Job,
  user: User | null = null,
  pageType: PageType = PAGE_TYPES.JOB_DETAIL,
) =>
  setEventParams({
    [DIMENSIONS.entity_id]: job.id,
    [DIMENSIONS.entity_type]: 'node',
    [DIMENSIONS.entity_subtype]: 'job_per_template',
    [DIMENSIONS.organization_name]:
      job.organizationProfile?.name ?? job.organization,
    [DIMENSIONS.title]: job.title,
    [DIMENSIONS.application_type]: job.applicationWorkflow,
    [DIMENSIONS.occupations]: concatArrayItems(job.occupationalField, 'label'),
    [DIMENSIONS.regions]: concatArrayItems(job.region, 'label'),
    [DIMENSIONS.administrative_area]: job.administrativeArea,
    [DIMENSIONS.employment_types]: concatArrayItems(
      job.employmentType,
      'label',
    ),
    // Job type source field depends on the specific project.
    [DIMENSIONS.importer_ID]: job['source'],
    [DIMENSIONS.upgrades]: job.jobUpgrades?.join(' | '),
    [DIMENSIONS.current_user_id]: user?.id,
    [DIMENSIONS.user_role]: concatArrayItems(user?.roles, 'targetId'),
    [DIMENSIONS.owner_id]: job.ownerId,
    [DIMENSIONS.organization_id]: job.organizationProfile?.organization?.id,
    [DIMENSIONS.page_type]: pageType,
    [DIMENSIONS.job_owner_frontend]: job?.jobPostingFrontend,
    [DIMENSIONS.salary_low]: job.schemaOrgSalary?.minValue?.number
      ? Math.round(Number(job.schemaOrgSalary.minValue.number))
      : undefined,
    [DIMENSIONS.salary_high]: job.schemaOrgSalary?.maxValue?.number
      ? Math.round(Number(job.schemaOrgSalary.maxValue.number))
      : undefined,
    [DIMENSIONS.salary_currency]:
      job.schemaOrgSalary?.minValue?.currency_code ??
      job.schemaOrgSalary?.maxValue?.currency_code ??
      job.schemaOrgSalary?.defaultCurrency,
    [DIMENSIONS.state]:
      job.workflowState.value === 'published' ? 'published' : 'unpublished',
    // Days since the publishing date
    [DIMENSIONS.job_age]: job.published
      ? Math.floor(
          (Date.now() - parseUniversalDate(job.published).getTime()) /
            (1000 * 60 * 60 * 24),
        )
      : undefined,
    [DIMENSIONS.product_type]: job.analyticsJobProductType ?? '(none)',
    ...getCustomJobDimensions(job, user),
  });

/**
 * Constructs statistics text for given job.
 *
 * @param {Job} job
 *  The job with statistics.
 * @param {IntlShape} intl
 *  IntlShape used for translations.
 * @returns {string}
 *  Meaningful statistics text.
 */
export const extractStatisticsText = (job: Job, intl: IntlShape): string => {
  const analyticsItems: string[] = [];

  if (job.analyticsJobVisits) {
    analyticsItems.push(
      `${job.analyticsJobVisits} ${
        job.analyticsJobVisits > 1
          ? intl.formatMessage({
              id: 'job_visits_multiple',
              defaultMessage: 'views',
            })
          : intl.formatMessage({
              id: 'job_visits_single',
              defaultMessage: 'view',
            })
      }`,
    );
  }

  if (job.analyticsJobApplyStart) {
    analyticsItems.push(
      `${job.analyticsJobApplyStart} ${
        job.analyticsJobApplyStart > 1
          ? intl.formatMessage({
              id: 'job_apply_start_multiple',
              defaultMessage: 'applications start',
            })
          : intl.formatMessage({
              id: 'job_apply_start_single',
              defaultMessage: 'application start',
            })
      }`,
    );
  }

  if (job.analyticsJobApplyComplete) {
    analyticsItems.push(
      `${job.analyticsJobApplyComplete} ${
        job.analyticsJobApplyComplete > 1
          ? intl.formatMessage({
              id: 'job_apply_complete_multiple',
              defaultMessage: 'applications completed',
            })
          : intl.formatMessage({
              id: 'job_apply_complete_single',
              defaultMessage: 'application completed',
            })
      }`,
    );
  }

  return `${intl.formatMessage({
    id: 'statistics',
    defaultMessage: 'Statistics',
  })}: ${
    analyticsItems.length > 0
      ? analyticsItems.join(' | ')
      : intl.formatMessage({
          id: 'n/a',
          defaultMessage: 'no data yet',
        })
  }`;
};
